import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { MissionSchema } from "../schemas/mission.schema";
import { MissionRewardType } from "../types/mission.type";
import { frontendRewardTypeToBackend } from "../utils/missionMapper";
import { MissionFormTransformer } from "../services/missionFormTransformer";

interface UseCreateMissionProps {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
}

export const useCreateMission = ({
  onSuccess,
  onError,
}: UseCreateMissionProps = {}) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ["createMission"],
    mutationFn: async (form: MissionSchema) => {
      const maxPerUser = form.allow_multiple_times ? form.max_times : 1;
      const payload = {
        mission_type: form.mission_type,
        display_name: form.name,
        description: form.description,
        status: form.status,
        minimum_loyalty: form.minimum_loyalty,
        available_from: MissionFormTransformer.parseDateField(form.start_date),
        available_until: MissionFormTransformer.parseDateField(form.end_date),
        maximum_completions_per_user: maxPerUser || null,
        establishment_company_branch_id:
          form.mission_type === "establishment" ? form.establishment?.id : null,
        requirements_attributes: form.conditions.map((cond) => {
          const conditionProperties = {
            ...(cond.properties || {}),
            ...(cond.type_label ? { label: cond.type_label } : {}),
            ...(cond.condition_group_label
              ? { condition_group_label: cond.condition_group_label }
              : {}),
            ...(cond.value_label ? { value_label: cond.value_label } : {}),
          };

          return {
            provider_klass: cond.provider_klass || cond.type_label,
            quantity: MissionFormTransformer.resolveRequirementQuantity(cond),
            condition_group_label: cond.condition_group_label || "general",
            properties: conditionProperties,
          };
        }),
        rewards_attributes: form.rewards.map((rew) => {
          const rewardProperties: Record<string, unknown> = {};
          if (rew.reward_label) rewardProperties.label = rew.reward_label;
          if (rew.product_name)
            rewardProperties.product_name = rew.product_name;
          const frontendRewardType = rew.reward_type as MissionRewardType;
          const backendRewardType =
            frontendRewardTypeToBackend(frontendRewardType);
          const isPointsReward = backendRewardType === null;
          const normalizedRewardId = !isPointsReward
            ? rew.product_id || rew.reward_id
            : undefined;
          return {
            reward_type: backendRewardType,
            reward_id: isPointsReward
              ? null
              : normalizedRewardId
                ? String(normalizedRewardId)
                : undefined,
            quantity: Number(rew.quantity ?? 1),
            properties: rewardProperties,
          };
        }),
      };
      const response = await privateAxios.post("/tmais/missions", {
        mission: payload,
      });
      return response;
    },
    onSuccess,
    onError,
  });
};
